/**
 * The live sound of a preview: the voice, the music bed and the effects, each through its own gain so the faders act the moment
 * they move, with an analyser behind the voice and the bed for the meters beside the timeline. Shared by the instant player and
 * the exact preview — the one draws its own picture, the other plays the finished render muted — so both sound the same way.
 */
import { CUE_SFX_GAIN, END_CARD_SFX_GAIN, OPENER_SFX_GAIN, musicGainAt } from "@hudhud/reel";
import { duckEnvelope, isFullscreenOpener, sfxCues, timelineMusicMutes, voiceFadeGain, type Clip } from "@hudhud/core";
import { liveLevels, pushLevel, rmsOf } from "./levels.js";

export type Audition = "mix" | "music" | "voice";
export interface SfxEvent { atMs: number; url: string; gain: number }

export const dbToGain = (db: number): number => Math.min(2, Math.pow(10, db / 20));

/** Every sound the clip plays, on the timeline's clock: the opener's, the end card's, one per cut when on, and the placed moments. */
export function sfxEventsOf(clip: Clip, sounds: { id: string; file: string }[], hold: number, speechMs: number): SfxEvent[] {
  const urlOf = (id: string | null) => { const snd = id ? sounds.find((x) => x.id === id) : null; return snd ? `/assets/sfx/${snd.file}` : null; };
  const out: SfxEvent[] = [];
  if (clip.hook.enabled && clip.sfx.hook) { const u = urlOf(clip.sfx.openerId); if (u) out.push({ atMs: (isFullscreenOpener(clip.hook.style) ? 0 : hold) + clip.hook.delayMs, url: u, gain: Math.min(1, OPENER_SFX_GAIN * dbToGain(clip.sfx.levels.opener)) }); }
  if (clip.endCard.enabled && clip.sfx.endCard) { const u = urlOf(clip.sfx.endCardId); if (u) out.push({ atMs: hold + speechMs, url: u, gain: Math.min(1, END_CARD_SFX_GAIN * dbToGain(clip.sfx.levels.endCard)) }); }
  for (const c of sfxCues(clip)) { const u = urlOf(c.soundId); if (u) out.push({ atMs: hold + c.atMs, url: u, gain: Math.min(1, CUE_SFX_GAIN * dbToGain(c.levelDb)) }); }
  return out.sort((a, b) => a.atMs - b.atMs);
}

/** What the gains follow: the clip, its timing, the bed's ducking envelope and mutes, and what the studio is auditioning. */
export interface MixInput {
  clip: Clip;
  /** ms of full-screen opener before the speech */
  hold: number;
  speechMs: number;
  totalMs: number;
  env: ReturnType<typeof duckEnvelope>;
  mutes: ReturnType<typeof timelineMusicMutes>;
  audition: Audition;
}

/**
 * The voice's and the bed's gains `ms` into the timeline. `voiceLevel` is the caller's base level for the voice (1 when the
 * file already carries the clip's level, the fader's gain and the piece-edge ease when it does not); the voice is silent
 * outside the speech and while the music alone is auditioned, the bed while the voice alone is.
 */
export function mixGains(i: MixInput, ms: number, voiceLevel: number, inSpeech: boolean): { voice: number; bed: number } {
  const voice = i.audition === "music" || !inSpeech ? 0 : voiceLevel * voiceFadeGain(ms - i.hold, i.clip.segments, i.clip.voiceFade);
  const bedOn = !!i.clip.music.trackId && !i.clip.music.muted && i.audition !== "voice" && !i.mutes.whole;
  const bed = bedOn ? Math.max(0, Math.min(1, musicGainAt(i.clip, i.env, ms, i.totalMs, i.hold, i.mutes.ranges))) : 0;
  return { voice, bed };
}

/** The graph: one gain per voice and bed (each metered after its fader), one gain per effect element. */
export interface MixGraph {
  ctx: AudioContext;
  voice: GainNode;
  bed: GainNode;
  sfx: Map<HTMLMediaElement, GainNode>;
  /** elements already given a source node: a media element can be wired once only */
  wired: WeakSet<HTMLMediaElement>;
  meters: { voice: AnalyserNode; bed: AnalyserNode; buf: Float32Array<ArrayBuffer> };
}

/** Builds the graph, or null where the page has no Web Audio (the levels then stay on the elements' own volume). */
export function createMixGraph(): MixGraph | null {
  try {
    const ctx = new AudioContext();
    const voice = ctx.createGain(), bed = ctx.createGain();
    voice.connect(ctx.destination); bed.connect(ctx.destination);
    // the meters listen after the faders, so they show what is heard
    const mk = () => { const an = ctx.createAnalyser(); an.fftSize = 512; an.smoothingTimeConstant = 0; return an; };
    const meters = { voice: mk(), bed: mk(), buf: new Float32Array(new ArrayBuffer(512 * 4)) };
    voice.connect(meters.voice); bed.connect(meters.bed);
    return { ctx, voice, bed, sfx: new Map(), wired: new WeakSet(), meters };
  } catch {
    return null;
  }
}

/** Routes a media element through the graph as the voice, the bed, or one effect (its own gain, silent until fired). Once only. */
export function wireElement(g: MixGraph, el: HTMLMediaElement, role: "voice" | "bed" | "sfx"): void {
  if (g.wired.has(el)) return;
  try {
    const src = g.ctx.createMediaElementSource(el);
    if (role === "sfx") { const gain = g.ctx.createGain(); gain.gain.value = 0; src.connect(gain); gain.connect(g.ctx.destination); g.sfx.set(el, gain); }
    else src.connect(role === "voice" ? g.voice : g.bed);
    g.wired.add(el);
  } catch { /* already the source of another graph: it keeps its own volume */ }
}

/** Reads the meters into the shared levels: what the voice and the bed sound like this frame, silence while paused. */
export function readMeters(g: MixGraph | null, playing: boolean, now = performance.now()): void {
  const mt = g?.meters;
  if (mt && playing) {
    mt.voice.getFloatTimeDomainData(mt.buf); pushLevel("voice", rmsOf(mt.buf), now);
    mt.bed.getFloatTimeDomainData(mt.buf); pushLevel("bed", rmsOf(mt.buf), now);
  } else if (liveLevels.voice || liveLevels.bed) { pushLevel("voice", 0, now); pushLevel("bed", 0, now); }
}

/**
 * Where the bed's own player should be for a moment of the reel: its section's start plus the reel's time, wrapped round the
 * section (the bed loops inside it). `trackSec` is the track's length, for a section that runs to its end; while that is still
 * unknown the bed is not wrapped.
 */
export function bedPositionSec(music: Pick<Clip["music"], "startMs" | "endMs">, ms: number, trackSec: number): number {
  const start = music.startMs / 1000;
  const end = music.endMs != null ? music.endMs / 1000 : trackSec;
  const at = Math.max(0, ms) / 1000;
  return start + (end > start ? at % (end - start) : at);
}

/** The most the picture's clock moves between two frames of plain playback, ms: a slow frame it then catches up from included. */
const CLOCK_STEP_MAX_MS = 1000;
/** The clock's own jitter backwards, ms. */
const CLOCK_BACK_SLACK_MS = 50;
/**
 * Whether the picture's clock moved in a way plain playback never does: back to the start on a loop, or anywhere on a seek. The
 * players that run on their own beside it (the bed) are then put back in step; in plain playback they are left alone, since
 * every correction is heard.
 */
export const clockJumped = (prevMs: number, ms: number): boolean => ms < prevMs - CLOCK_BACK_SLACK_MS || ms - prevMs > CLOCK_STEP_MAX_MS;
